import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  MenuItem,
  Box,
  IconButton,
  Typography,
  Alert,
  AlertTitle
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import JBrowseService from '../services/jbrowse.service';

const EditSessionModal = (props) => {
  const {
    setLoading,
    currentUser,
    selectedCrop,
    setShowEditModal,
    selectedSession,
    setSelectedSession,
    setCurrentSessions,
    errorEditMessage,
    setErrorEditMessage,
    successEditMessage,
    setSuccessEditMessage,
    chromosomes
  } = props;

  const [formData, setFormData] = useState({
    name: selectedSession.session.name,
    location: selectedSession.location
  });

  const [chromosome, setChromosome] = useState(chromosomes[0]);
  const [start, setStart] = useState('');
  const [stop, setStop] = useState('');

  const handleInputChange = (event) => {
    const { name, value } = event.target;
    setFormData({
      ...formData,
      [name]: value
    });
  };

  const handleChromosomeChange = (event) => {
    setChromosome(event.target.value);
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    setLoading(true);

    const location = `${chromosome}:${start}..${stop}`;

    const updatedSession = {
      ...selectedSession,
      session: {
        ...selectedSession.session,
        name: formData.name
      },
      location
    };

    setSelectedSession(updatedSession);

    JBrowseService.updateSessionForUser(updatedSession).then(
      () => {
        setShowEditModal(false);
        setLoading(false);

        JBrowseService.getSavedSessions({ user: currentUser }).then(
          (response) => {
            setCurrentSessions(response.result.reverse());
            setLoading(false);
          },
          () => {
            setLoading(false);
          }
        );
      },
      () => {
        setLoading(false);
      }
    );
  };

  const closeModal = () => {
    setShowEditModal(false);
  };

  useEffect(() => {
    if (selectedSession && selectedSession.location) {
      const locationParts = selectedSession.location.split(':');
      const chromosomePart = locationParts[0];
      const positions = locationParts[1].split(/[-..]+/);

      setChromosome(chromosomePart);
      setStart(positions[0]);
      setStop(positions[1]);
    } else {
      setChromosome(chromosomes?.[0] || '');
      setStart('');
      setStop('');
    }
  }, [selectedSession, chromosomes]);

  return (
    <Dialog
      open={Boolean(selectedSession)}
      onClose={closeModal}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        elevation: 5,
        sx: { overflowY: 'visible', boxShadow: 10 }
      }}
    >
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Typography variant="h6">Edit Session</Typography>
        <IconButton onClick={closeModal}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers>
        <Box
          component="form"
          sx={{ '& .MuiTextField-root': { my: 1 } }}
          noValidate
          autoComplete="off"
        >
          <TextField
            required
            id="name"
            label="Session Name"
            name="name"
            value={formData.name}
            onChange={handleInputChange}
            variant="outlined"
            fullWidth
          />
          {chromosomes && chromosomes.length > 0 && (
            <TextField
              select
              label="Chromosome"
              value={chromosome}
              onChange={handleChromosomeChange}
              variant="outlined"
              fullWidth
            >
              {chromosomes.map((option) => (
                <MenuItem key={option} value={option}>
                  {option}
                </MenuItem>
              ))}
            </TextField>
          )}
          <TextField
            required
            id="start"
            label="Start"
            name="start"
            type="number"
            value={start}
            onChange={(e) => setStart(e.target.value)}
            variant="outlined"
            fullWidth
          />
          <TextField
            required
            id="stop"
            label="Stop"
            name="stop"
            type="number"
            value={stop}
            onChange={(e) => setStop(e.target.value)}
            variant="outlined"
            fullWidth
          />
        </Box>

        {successEditMessage && (
          <Alert
            severity="success"
            sx={{ mt: 2 }}
            onClose={() => setSuccessEditMessage(false)}
          >
            <AlertTitle>Success</AlertTitle>
            Successfully updated a session.
          </Alert>
        )}

        {errorEditMessage && (
          <Alert
            severity="error"
            sx={{ mt: 2 }}
            onClose={() => setErrorEditMessage(false)}
          >
            <AlertTitle>Error</AlertTitle>
            There was an error updating a session, refresh and try again.
          </Alert>
        )}
      </DialogContent>

      <DialogActions>
        <Button
          type="button"
          variant="contained"
          color="success"
          onClick={handleSubmit}
        >
          Save Updates
        </Button>
        <Button
          variant="outlined"
          color="primary"
          onClick={closeModal}
        >
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default EditSessionModal;
